import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { App as AppMobile } from '@capacitor/app';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { ErrorBoundary } from 'react-error-boundary';
import moment from 'moment-timezone';
import semver from 'semver';
import { FontLoadedProvider } from './contexts/FontLoadContext';
import Error500Page from './components/pages/general/Error500Page';
import ThemeConfig from '@app/theme';
import GlobalStyles from './theme/globalStyles';

import DeepLinksListener from './components/features/companies/CompanySelectionView/DeepLinksListener';
import LoadingScreen, { ProgressBarStyle } from './components/loadings/LoadingScreen';
import { DownloadAppModal } from './components/modals';
import NotistackProvider from './components/NotistackProvider';
import RtlLayout from './components/RtlLayout';
import SocketEvents from './components/SocketEvents';
import ThemeLocalization from './components/ThemeLocalization';
import ThemePrimaryColor from './components/ThemePrimaryColor';

import useAuth from './hooks/useAuth';

import { getClockingDeviceSites } from './redux/slices/clockings';
import { getCompanies } from './redux/slices/companies';
import { getMissions } from './redux/slices/missions';
import { getResource, getResourceContracts } from './redux/slices/resources';

import { getLogo, getResourceFilesNotifications } from './redux/slices/files';
import { useDispatch } from './redux/store';

import Router from './routes';

import track from './utils/analytics';

const timezone = moment.tz.guess(true);
moment.tz.setDefault(timezone);

const App = () => {
  const { hasLogout, isDevicePaired, isAuthenticated, isInitialized, isResource, login, user, features, isManager } =
    useAuth();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [openModal, setOpenModal] = useState(false);

  // If SocketContext is ready and not logged OR
  // If SocketContext is ready and logged in and resource role and resource's infos loaded OR
  // If SocketContext is ready and logged in and not resource role
  const isAppReady = isInitialized && (!isAuthenticated || user?.role === 'resource' || user?.role !== 'resource');
  useEffect(() => {
    if (window.env.isApp) {
      // https://capacitorjs.com/docs/apis/app
      AppMobile.addListener('backButton', ({ canGoBack } = {}) => {
        if (canGoBack) {
          window.history.back();
        }
      });

      return () => AppMobile.removeAllListeners();
    }
  }, []);

  useEffect(() => {
    if (isInitialized && window.env.isApp) {
      // Vérification de la présence d'une mise à jour
      AppUpdate.getAppUpdateInfo()
        .then((updateInfo) => {
          const {
            updateAvailability,
            currentVersionCode,
            currentVersionName,
            availableVersionCode,
            availableVersionName
          } = updateInfo;

          let updateAvailable = false;

          if (window.env.isIos) {
            updateAvailable = semver.lt(currentVersionName, availableVersionName);
          } else if (window.env.isAndroid) {
            updateAvailable = parseInt(currentVersionCode, 10) < parseInt(availableVersionCode, 10);
          }

          console.debug({ getAppUpdateInfo: { updateInfo, updateAvailability, updateAvailable } });

          setOpenModal(updateAvailable);
        })
        .catch(console.error);
    }
  }, [isInitialized]);

  // Auto-login pour l'application Comète On Time (kiosque) :
  // si l'appareil est pairé mais qu'aucune session n'est active (et que
  // l'utilisateur n'a pas explicitement fait logout), on tente une
  // reconnexion silencieuse.
  useEffect(() => {
    if (!isInitialized) return;
    if (!(window.env.isClockingApp && isDevicePaired && !isAuthenticated && !hasLogout)) return;
    login().catch(console.error);
  }, [isInitialized, isDevicePaired, isAuthenticated, hasLogout, login]);

  // Chargement des données initiales après authentification :
  // logo, contrats/missions agent, notifications fichiers, sites du
  // kiosque OnTime, sociétés accessibles. Re-run uniquement quand l'auth
  // ou le profil utilisateur changent.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) return;

    const isPartner = user.role === 'manager_partner';

    dispatch(getLogo());

    if (isResource) {
      dispatch(getResource({ resourceId: user?.userResourceId }));
      dispatch(getResourceContracts({ resourceId: user?.userResourceId, month: moment().format('YYYYMM') }));
      if (features?.notifMission) {
        dispatch(getMissions());
      }
    }
    if (!isManager && features?.documentConsultations) {
      dispatch(getResourceFilesNotifications());
    }
    if (window.env.isClockingApp) {
      dispatch(getClockingDeviceSites({ uuid: window.env.uuid }));
    }

    dispatch(getCompanies({ companyId: isPartner ? user.userSubCompanyId : null }));
  }, [
    dispatch,
    isInitialized,
    isAuthenticated,
    user,
    isResource,
    isManager,
    features?.notifMission,
    features?.documentConsultations
  ]);

  const handleError = (err, { componentStack }) => {
    track.exception({ message: err.message, stack: err.stack, componentStack }, true);
  };

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <ThemeLocalization>
          <RtlLayout>
            <NotistackProvider>
              <FontLoadedProvider>
                <GlobalStyles />
                <ProgressBarStyle />
                <ErrorBoundary FallbackComponent={Error500Page} onError={handleError}>
                  {isAppReady ? (
                    <>
                      <DownloadAppModal open={openModal} onClose={() => setOpenModal(false)} />
                      <SocketEvents />
                      <DeepLinksListener />
                      <Router />
                    </>
                  ) : (
                    <LoadingScreen />
                  )}
                </ErrorBoundary>
              </FontLoadedProvider>
            </NotistackProvider>
          </RtlLayout>
        </ThemeLocalization>
      </ThemePrimaryColor>
    </ThemeConfig>
  );
};

export default App;
